import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, TextField, Chip, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, PlayArrow as PlayArrowIcon, Save as SaveIcon, FilterAlt as FilterAltIcon, Tune as TuneIcon } from '@mui/icons-material';
import { QueryGroup, QueryField, QueryRule, QueryExecutionResultWithConditions } from '../../../../types/QueryBuilder';
import QueryRuleGroup from './QueryRuleGroup';
import SimpleQueryGroup from './SimpleQueryGroup';
import GroupSelector from './GroupSelector';
import ConfirmDialog from '../../ListImport/components/ConfirmDialog';

export type QueryBuilderMode = 'simple' | 'advanced';

interface CreateQueryTabProps {
    mode: QueryBuilderMode;
    onModeChange: (mode: QueryBuilderMode) => void;
    groups: QueryGroup[];
    categories: string[];
    fields: QueryField[];
    simpleFields: QueryField[];
    simpleRules: QueryRule[];
    onUpdateSimpleField: (fieldName: string, data: Partial<QueryRule>) => void;
    queryJSON: QueryExecutionResultWithConditions | null;
    isValid: boolean;
    executingQuery: boolean;
    saving: boolean;
    selectedGroupId: number | null;
    newGroupName?: string;
    availableGroups: Array<{ id: number; description: string }>;
    loadingGroups: boolean;
    descResetKey?: number;
    onRefetchGroups: () => void | Promise<void>;
    onQueryDescriptionChange: (description: string) => void;
    onGroupIdChange: (groupId: number | null, groupName?: string) => void;
    onCreateGroup: (groupName: string) => void | Promise<void>;
    onAddRule: (groupId: string) => void;
    onUpdateRule: (groupId: string, ruleId: string, data: object) => void;
    onRemoveRule: (groupId: string, ruleId: string) => void;
    onAddGroup: () => void;
    onRemoveGroup: (groupId: string) => void;
    onReset: () => void;
    onExecute: () => void;
    onSave: () => void;
}

const DescriptionField = React.memo(({
    value,
    onChange,
}: {
    value: string;
    onChange: (value: string) => void;
}) => {
    return (
        <TextField
            size="small"
            label="Description de la requête"
            name="queryDescription"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Ex: Clients fidèles - Carte FIDELITE"
            sx={{ flexGrow: 1, minWidth: '300px' }}
        />
    );
});

export function CreateQueryTab({
    mode,
    onModeChange,
    groups,
    categories,
    fields,
    simpleFields,
    simpleRules,
    onUpdateSimpleField,
    queryJSON,
    isValid,
    executingQuery,
    saving,
    selectedGroupId,
    newGroupName,
    availableGroups,
    loadingGroups,
    descResetKey,
    onRefetchGroups,
    onQueryDescriptionChange,
    onGroupIdChange,
    onCreateGroup,
    onAddRule,
    onUpdateRule,
    onRemoveRule,
    onAddGroup,
    onRemoveGroup,
    onReset,
    onExecute,
    onSave,
}: CreateQueryTabProps) {
    const formRef = useRef<HTMLFormElement>(null);
    const [queryDescription, setQueryDescription] = useState('');
    const [resetModalOpen, setResetModalOpen] = useState(false);

    useEffect(() => {
        setQueryDescription('');
    }, [descResetKey]);

    const handleExecute = useCallback(() => {
        if (!queryDescription.trim()) return;
        onExecute();
    }, [onExecute, queryDescription]);

    const handleSave = useCallback(() => {
        if (!queryDescription.trim()) return;
        onSave();
    }, [onSave, queryDescription]);

    const handleResetClick = useCallback(() => {
        setResetModalOpen(true);
    }, []);

    const handleResetConfirm = useCallback(() => {
        setResetModalOpen(false);
        onReset();
    }, [onReset]);

    const hasDescription = queryDescription.trim().length > 0;

    const renderQueryToolbar = () => (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
            <form ref={formRef}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <DescriptionField value={queryDescription} onChange={(v) => { setQueryDescription(v); onQueryDescriptionChange(v); }} />

                    <GroupSelector
                        selectedGroupId={selectedGroupId}
                        newGroupName={newGroupName}
                        availableGroups={availableGroups}
                        loading={loadingGroups}
                        onGroupIdChange={onGroupIdChange}
                        onCreateGroup={onCreateGroup}
                        onRefetchGroups={onRefetchGroups}
                    />

                    <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleResetClick}
                            sx={{ textTransform: 'none', borderColor: '#6c757d', color: '#6c757d' }}
                        >
                            Réinitialiser
                        </Button>
                        <Tooltip title={!hasDescription ? 'Veuillez saisir une description de requête' : ''}>
                            <span>
                                <Button
                                    variant="contained"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={handleExecute}
                                    disabled={executingQuery || !isValid || !hasDescription}
                                    sx={{ textTransform: 'none', bgcolor: '#556ee6' }}
                                >
                                    {executingQuery ? 'Exécution...' : 'Exécuter'}
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving || !isValid || !hasDescription}
                            sx={{ textTransform: 'none', color: '#28a745', borderColor: '#28a745' }}
                        >
                            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                        </Button>
                    </Box>
                </Box>
            </form>
        </Paper>
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(_, newMode: QueryBuilderMode | null) => newMode && onModeChange(newMode)}
                    sx={{
                        '& .MuiToggleButton-root': { textTransform: 'none', px: 3, fontWeight: 500 },
                        '& .MuiToggleButton-root.Mui-selected': { bgcolor: '#556ee6', color: 'white', '&:hover': { bgcolor: '#4457c9' } }
                    }}
                >
                    <ToggleButton value="simple">
                        <FilterAltIcon sx={{ mr: 1 }} fontSize="small" />
                        Requêteur simple
                    </ToggleButton>
                    <ToggleButton value="advanced">
                        <TuneIcon sx={{ mr: 1 }} fontSize="small" />
                        Requêteur avancé
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {renderQueryToolbar()}

            {mode === 'simple' ? (
                <SimpleQueryGroup
                    fields={simpleFields}
                    rules={simpleRules}
                    onUpdateField={onUpdateSimpleField}
                />
            ) : (
                <>
                    {groups.map((group, index) => (
                        <Box key={group.id}>
                            <QueryRuleGroup
                                group={group}
                                categories={categories}
                                fields={fields}
                                onAddRule={() => onAddRule(group.id)}
                                onUpdateRule={(ruleId, data) => onUpdateRule(group.id, ruleId, data)}
                                onRemoveRule={(ruleId) => onRemoveRule(group.id, ruleId)}
                                onRemoveGroup={() => onRemoveGroup(group.id)}
                                showCombinator={index > 0}
                                canRemoveGroup={groups.length > 1}
                            />
                            {index < groups.length - 1 && (
                                <Box sx={{ textAlign: 'center', my: 2 }}>
                                    <Chip label="OU" sx={{ bgcolor: '#ffc107', color: 'white', fontWeight: 600 }} />
                                </Box>
                            )}
                        </Box>
                    ))}

                    {groups.length < 2 && (
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={onAddGroup}
                            sx={{ mt: 2, textTransform: 'none', borderColor: '#556ee6', color: '#556ee6', borderStyle: 'dashed' }}
                        >
                            Ajouter un groupe (OU)
                        </Button>
                    )}
                </>
            )}

            {renderQueryToolbar()}

            {queryJSON && (
                <Paper elevation={0} sx={{ p: 2, mt: 3, border: '1px solid #e0e0e0' }}>
                    {queryJSON.count !== undefined && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                                🎯 {queryJSON.count} résultat{queryJSON.count > 1 ? 's' : ''} trouvé{queryJSON.count > 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        JSON de la requête :
                    </Typography>
                    <Box
                        component="pre"
                        sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto', fontSize: '0.875rem', maxHeight: '400px' }}
                    >
                        {JSON.stringify(queryJSON, null, 2)}
                    </Box>
                </Paper>
            )}

            <ConfirmDialog
                open={resetModalOpen}
                title="Réinitialiser la requête"
                message="Voulez-vous vraiment réinitialiser tous les champs de la requête ?"
                confirmText="Réinitialiser"
                confirmColor="error"
                onConfirm={handleResetConfirm}
                onCancel={() => setResetModalOpen(false)}
            />
        </Box>
    );
}

export default CreateQueryTab;
